'use client'

import type { CSSProperties } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'

import { GRAFTCODE_ONBOARDING_GOALS, type GraftcodeOnboardingGoalId } from '../../graftcode-onboarding.const'
import { GRAFTCODE_ONBOARDING_TEST_IDS } from '../../graftcode-onboarding-test-ids.const'

import styles from './GraftcodeOnboardingWizard.module.scss'

type GraftcodeOnboardingGoalStepProps = {
  selectedGoalId: GraftcodeOnboardingGoalId
  onSelectGoal: (goalId: GraftcodeOnboardingGoalId) => void
  onContinue: () => void
}

export function GraftcodeOnboardingGoalStep({
  selectedGoalId,
  onSelectGoal,
  onContinue,
}: GraftcodeOnboardingGoalStepProps) {
  const t = useTranslations('graftcodeOnboarding')

  return (
    <div className={styles.main}>
      <div className={styles.content}>
        <h1
          className={`${styles.title} ${styles.reveal}`}
          id="graftcode-onboarding-title"
          style={{ '--ob-i': 0 } as CSSProperties}
        >
          {t('wizard.goalTitle')}
        </h1>
        <div className={styles.goals} role="radiogroup" aria-labelledby="graftcode-onboarding-title">
          {GRAFTCODE_ONBOARDING_GOALS.map((goal, index) => {
            const selected = goal.id === selectedGoalId
            return (
              <button
                key={goal.id}
                type="button"
                className={`${styles.goalCard} ${styles.reveal} ${selected ? styles.goalCardSelected : ''}`}
                style={{ '--ob-i': index + 1 } as CSSProperties}
                role="radio"
                aria-checked={selected}
                data-testid={GRAFTCODE_ONBOARDING_TEST_IDS.goalCard(goal.id)}
                onClick={() => onSelectGoal(goal.id)}
              >
                <span className={styles.goalHead}>
                  <span className={styles.goalIcon}>
                    <Image src={goal.icon} alt="" width={24} height={24} />
                  </span>
                  <span className={styles.goalTitle}>{t(goal.titleKey)}</span>
                </span>
                <span className={styles.goalPoints}>
                  {goal.pointKeys.map((key) => (
                    <span key={key}>- {t(key)}</span>
                  ))}
                </span>
              </button>
            )
          })}
        </div>
      </div>
      <button
        type="button"
        className={`${styles.primaryButton} ${styles.reveal}`}
        style={{ '--ob-i': 3 } as CSSProperties}
        data-testid={GRAFTCODE_ONBOARDING_TEST_IDS.continue}
        onClick={onContinue}
      >
        {t('wizard.continue')}
      </button>
    </div>
  )
}
